import type { Disposable } from "@/shared/contracts/lifecycle";
import { HoverChargeExecutionController } from "@/features/hoverIdle/core/hoverChargeExecutionController";
import { HoverIntentController } from "@/features/hoverIdle/core/hoverIntentController";
import type { HoverPointerModel } from "@/features/hoverIdle/input/hoverPointerModel";

export interface MpmHeaderActionChargeControllerOptions {
  readonly element: HTMLElement;
  readonly pointerModel: HoverPointerModel;
  readonly getChargeMs: () => number;
  readonly onExecute: () => void;
}

export interface MpmHeaderActionChargeState {
  readonly disabled: boolean;
}

export class MpmHeaderActionChargeController implements Disposable {
  private readonly element: HTMLElement;
  private readonly intent: HoverIntentController;
  private readonly chargeController: HoverChargeExecutionController;
  private readonly getChargeMs: () => number;
  private readonly onExecute: () => void;
  private disabled = true;

  constructor(options: MpmHeaderActionChargeControllerOptions) {
    this.element = options.element;
    this.getChargeMs = options.getChargeMs;
    this.onExecute = options.onExecute;
    this.applyRestingState();
    this.chargeController = new HoverChargeExecutionController({
      getDurationMs: this.getChargeMs,
      canExecute: () => !this.disabled,
      onProgress: (progress) => this.applyChargeProgress(progress),
      onExecute: () => {
        this.onExecute();
        this.applyRestingState();
      },
    });
    this.intent = new HoverIntentController({
      target: options.element,
      pointerModel: options.pointerModel,
      onHoverChange: (isHovering) => this.handleHoverChange(isHovering),
    });
  }

  sync(state: MpmHeaderActionChargeState): void {
    this.disabled = state.disabled;
    this.chargeController.syncCanExecute();
    if (this.chargeController.getProgress() === 0) {
      this.applyRestingState();
    }
  }

  cancelCharge(): void {
    this.chargeController.cancel();
    this.applyRestingState();
  }

  clearPointerHover(): void {
    this.intent.clearPointerHover();
  }

  refresh(options?: { readonly forceEmit?: boolean }): void {
    this.intent.refresh(options);
  }

  dispose(): void {
    this.chargeController.dispose();
    this.intent.dispose();
  }

  private handleHoverChange(isHovering: boolean): void {
    this.chargeController.setHovered(isHovering);
    if (isHovering && !this.disabled) {
      this.element.dataset.state = "charging";
      this.element.dataset.disabled = "false";
      this.element.setAttribute("aria-disabled", "false");
      return;
    }
    if (!isHovering && this.chargeController.getProgress() === 0) {
      this.applyRestingState();
    }
  }

  private applyRestingState(): void {
    this.element.dataset.disabled = this.disabled ? "true" : "false";
    this.element.dataset.state = this.disabled ? "disabled" : "idle";
    this.element.style?.setProperty("--hover-charge-progress", "0");
    this.element.setAttribute(
      "aria-disabled",
      this.disabled ? "true" : "false",
    );
  }

  private applyChargeProgress(progress: number): void {
    this.element.style?.setProperty(
      "--hover-charge-progress",
      String(progress),
    );
    this.element.dataset.disabled = this.disabled ? "true" : "false";
    if (this.disabled) {
      this.element.dataset.state = "disabled";
    } else {
      this.element.dataset.state = progress === 0 ? "idle" : "charging";
    }
    this.element.setAttribute(
      "aria-disabled",
      this.disabled ? "true" : "false",
    );
  }
}
